/* eslint-disable react-hooks/exhaustive-deps */
import classNames from "classnames";
import {
  AwesomeTableComponent,
  Button,
  IColumnsProps,
  Icon,
  InputText,
  InputTextPassword,
  Notifications,
  TimeUtils,
  UrlUtils,
  ViewLabelStatus,
} from "d-react-components";
import { useFormik } from "formik";
import { find, forEach, map, toUpper } from "lodash";
import { useEffect, useRef, useState } from "react";
import { StringParam, useQueryParam } from "use-query-params";
import * as Yup from "yup";
import UserAvatarName from "../components/UserAvatarName";
import {
  LANGUAGE_DEFAULT,
  QC_PROCESS_STATUS,
  QC_PROCESS_STATUS_LIST,
} from "../constant/app";
import Messages from "../languages/Messages";
import QcAPI from "../network/api/qc-process";

enum ViewMode {
  AUTH = "auth",
  MAIN = "main",
}
interface ILanguageItem {
  lang: string;
}

const LanguageItem = ({ lang }: ILanguageItem) => {
  const { locale = LANGUAGE_DEFAULT } = UrlUtils.getQuery();
  const classNameLanguage = classNames("mx-1 text-white cursor-pointer");

  const onChangeLanguage = () => {
    UrlUtils.addQuery({ locale: lang });
  };

  return (
    <div
      className={classNames(classNameLanguage, {
        "app-header__language-selecting": locale === lang,
      })}
      onClick={onChangeLanguage}
    >
      {toUpper(lang)}
    </div>
  );
};

const AppLayoutContext = ({ children }: any) => {
  return <div>{children}</div>;
};

const LoginSchema = Yup.object().shape({
  username: Yup.string().required("This field is required"),
  password: Yup.string().required("This field is required"),
});

const StationInfo = ({ station }: any) => {
  return (
    <div
      className="text-primary px-2 py-1 border border-primary rounded-1 w-auto small flex-center"
      style={{ maxWidth: "40px" }}
    >
      {station?.name ?? "N/A"}
    </div>
  );
};

const DefectionIcon = () => (
  <svg
    className="mr-1"
    width="15"
    height="16"
    viewBox="0 0 30 31"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.7109 22.6957H22.547L21.448 24.3287C21.3015 24.5448 21.2165 24.7966 21.2023 25.0573C21.1881 25.318 21.2452 25.5776 21.3675 25.8083C21.4897 26.039 21.6725 26.232 21.8962 26.3666C22.1199 26.5012 22.376 26.5723 22.6371 26.5723H22.6401C22.8749 26.5723 23.1067 26.5195 23.3184 26.4178C23.53 26.316 23.7161 26.168 23.8628 25.9846L27.2439 21.7576C27.3502 21.6247 27.4082 21.4594 27.4082 21.2891C27.4082 21.1188 27.3502 20.9536 27.2439 20.8206L23.8624 16.5939C23.7157 16.4105 23.5297 16.2625 23.318 16.1607C23.1064 16.059 22.8745 16.0062 22.6397 16.0062H22.6371C22.376 16.0062 22.1199 16.0773 21.8962 16.2119C21.6726 16.3465 21.4898 16.5395 21.3675 16.7701C21.2453 17.0008 21.1882 17.2604 21.2024 17.521C21.2166 17.7817 21.3015 18.0336 21.448 18.2496L22.5473 19.8832H11.7109C10.038 19.8832 8.4337 19.2187 7.25081 18.0358C6.06793 16.8529 5.40339 15.2486 5.40339 13.5757C5.40339 11.9029 6.06793 10.2985 7.25081 9.11563C8.4337 7.93274 10.038 7.2682 11.7109 7.2682H14.7779C15.1509 7.2682 15.5086 7.12005 15.7723 6.85632C16.036 6.5926 16.1842 6.23491 16.1842 5.86195C16.1842 5.48899 16.036 5.13131 15.7723 4.86758C15.5086 4.60386 15.1509 4.4557 14.7779 4.4557H11.7109C9.29212 4.4557 6.97241 5.41656 5.26208 7.12689C3.55174 8.83722 2.59089 11.1569 2.59089 13.5757C2.59089 15.9945 3.55174 18.3142 5.26208 20.0245C6.97241 21.7348 9.29212 22.6957 11.7109 22.6957Z"
      fill="black"
    />
  </svg>
);

function AppLayout() {
  const [viewMode, setViewMode] = useState(ViewMode.AUTH);
  const tableRef = useRef<any>();
  const [stationId, setStationId] = useQueryParam("id", StringParam);
  const [stationInfo, setStationInfo] = useState<any>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [previousData, setPreviousData] = useState<any[]>([]);

  useEffect(() => {
    if (stationInfo?.name) {
      document.title = `Station ${stationInfo.name} - QC Monitor | Sales Infinity X`;
    } else {
      document.title = "QC Monitor | Sales Infinity X";
    }
  }, [stationInfo]);

  const getDefaultValues = () => {
    const loginData = localStorage.getItem(stationId ?? "");
    const { username, password } = JSON.parse(loginData ?? "{}");
    return {
      username,
      password,
      station_id: stationId,
    };
  };

  const playNotificationSound = () => {
    try {
      // Create audio element and play notification sound
      const audio = new Audio("/sound.mp3");
      audio.volume = 0.5; // Set volume to 50%
      audio.play().catch((error) => {
        console.warn("Could not play notification sound:", error);
        // Fallback: Use Web Audio API to generate a simple beep
        const audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = 800; // 800Hz frequency
        oscillator.type = "sine";

        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          audioContext.currentTime + 0.5
        );

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      });
    } catch (error) {
      console.warn("Could not create audio context:", error);
    }
  };

  const authForm = useFormik<any>({
    initialValues: { station_id: stationId } as any,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: LoginSchema,
    onSubmit: (values: any) => {
      setViewMode(ViewMode.MAIN);
    },
  });
  const formValues = authForm?.values;
  const formErrors = authForm?.errors;

  useEffect(() => {
    const loginData = localStorage.getItem(stationId ?? "");
    const { username, password } = JSON.parse(loginData ?? "{}");

    if (username && password) {
      authForm.setValues({
        ...formValues,
        username,
        password,
      });

      setViewMode(ViewMode.MAIN);
    }
  }, []);

  const fetchData = async () => {
    if (!formValues?.station_id) return;

    setLoading(true);
    try {
      const response = await QcAPI.monitorList(formValues, {
        pageIndex: 1,
        pageSize: 1000,
      });
      const transformed = transformer(response);

      // Check for new data items by comparing with previous data
      if (previousData.length > 0) {
        const newItems = transformed.filter((newItem: any) => {
          // Skip separator items
          if (newItem.isSeparator) return false;

          // Check if this item exists in previous data
          return !previousData.some((prevItem: any) => {
            if (prevItem.isSeparator) return false;
            return (
              prevItem.id === newItem.id &&
              prevItem.serial_number === newItem.serial_number
              // prevItem.updated_at === newItem.updated_at
            );
          });
        });

        // If there are new items, play notification sound
        if (newItems.length > 0) {
          console.log(`New data items detected: ${newItems.length} items`);
          playNotificationSound();
        }
      }

      setDataSource(transformed);
      setPreviousData(transformed); // Update previous data for next comparison
      localStorage.setItem(stationId ?? "", JSON.stringify(formValues));
    } catch (error) {
      Notifications.showSuccess(
        JSON.stringify((error as any)?.response?.data?.message)
      );
      setViewMode(ViewMode.AUTH);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === ViewMode.MAIN) {
      fetchData();
      const intervalId = setInterval(() => {
        fetchData();
      }, 10000);

      return () => clearInterval(intervalId);
    }
  }, [viewMode, formValues]);

  const columns: IColumnsProps = [
    {
      title: Messages.id,
      dataIndex: "id",
      render: (id: any, item: any) => {
        if (item.isSeparator) {
          return null;
        }
        return id;
      },
    },
    {
      title: Messages.serialNo,
      dataIndex: "serial_number",
      render: (serial_number: any, item: any) => {
        if (item.isSeparator) {
          return null;
        }
        return serial_number;
      },
    },
    {
      title: Messages.product,
      dataIndex: "product",
      render: (product: any, item: any) => {
        if (item.isSeparator) {
          return null;
        }
        return (
          <div>
            <div>{product?.name ?? "N/A"}</div>
            <div>{`${Messages.sku}: ${product?.sku ?? "N/A"}`}</div>
            <div>{`${Messages.model}: ${product?.model?.name ?? "N/A"}`}</div>
          </div>
        );
      },
    },
    {
      title: Messages.previousQc,
      dataIndex: "",
      render: (qc: any, item: any) => {
        if (item.isSeparator) {
          return null;
        }
        return (
          <div>
            <StationInfo station={qc?.prev_qc_station} />
            <ViewLabelStatus
              status={qc?.prev_station_status ?? "N/A"}
              listStatus={QC_PROCESS_STATUS_LIST}
              getLabel={(item: any) => Messages[item.label]}
              className="mt-1"
            />
          </div>
        );
      },
    },
    {
      title: Messages.currentQc,
      dataIndex: "",

      render: (qc: any, item: any) => {
        if (item.isSeparator) {
          return null;
        }

        return (
          <div>
            <StationInfo station={qc?.qc_station} />
            <ViewLabelStatus
              status={qc?.qc_station_status ?? "N/A"}
              listStatus={QC_PROCESS_STATUS_LIST}
              getLabel={(item: any) => Messages[item.label]}
              className="mt-1"
            />
          </div>
        );
      },
    },
    {
      title: Messages.sendTo,
      dataIndex: "",
      render: (qc: any, item: any) => {
        if (item.isSeparator) {
          return null;
        }
        return (
          qc?.send_to_station && <StationInfo station={qc?.send_to_station} />
        );
      },
    },

    {
      title: Messages.defectionAndRework,
      dataIndex: "",
      render: (qc: any) => {
        console.log("qc info", qc);
        return (
          <div
            className="bg-error-10 px-3 rounded-1"
            style={{ minWidth: "250px" }}
          >
            {map(qc.info, (info, index) => {
              const { defection, rework, workers } = info;
              return (
                <div className="py-2 border-bottom">
                  <div className="small text-error">
                    {`${rework?.name} (${Messages.code}: ${rework?.code})`}
                  </div>
                  <div className="d-flex flex-column">
                    <div className="small" style={{ display: "inline-flex" }}>
                      <DefectionIcon />
                      {`${defection?.name} (${Messages.code}: ${rework?.code})`}
                    </div>
                    {!!workers?.length && (
                      <div className="small" style={{ display: "inline-flex" }}>
                        <DefectionIcon />
                        <div className="d-flex flex-column ">
                          {map(workers, (workerId) => {
                            const worker = find(
                              qc?.workers,
                              (worker: any) => worker?.id === workerId
                            );
                            return (
                              <div className="d-flex flex-row align-items-center">
                                <img
                                  src={worker?.avatar}
                                  className="rounded-1"
                                  style={{ width: "20px", height: "20px" }}
                                  alt={`${worker?.first_name} ${worker?.last_name} avatar`}
                                />
                                <div className="ml-1">
                                  {`${worker?.first_name} ${worker?.last_name} (${worker?.employee_id})`}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      },
    },

    {
      title: Messages.updatedAt,
      dataIndex: "updated_at",

      render: (updated_at: any, item: any) => {
        if (item.isSeparator) {
          return null;
        }
        return TimeUtils.toDateTime(updated_at);
      },
    },
    {
      title: Messages.qcBy,
      dataIndex: "qc_by",

      render: (by: any, item: any) => {
        if (item.isSeparator) {
          return null;
        }

        if (item.qc_station_status === "pending") {
          return <UserAvatarName user={item.prev_qc_by} />;
        } else {
          return <UserAvatarName user={by} />;
        }
      },
    },

    {
      title: "QC Layer Specs",
      dataIndex: "",
      render: (qc: any, item: any) => {
        if (item.isSeparator) {
          return null;
        }
        return (
          <img
            src={qc?.product?.layers?.spec_image_qc}
            style={{ width: "150px", height: "150px" }}
            alt="QC Layer Specs"
          />
        );
      },
    },
  ];

  const transformer = (res: any) => {
    const qcProcessList = res?.data?.data?.monitor ?? [];
    const station = res?.data?.data?.station;
    setStationInfo(station);

    let transformed: any[] = [];
    qcProcessList.forEach((item: any) => {
      forEach(item.detail, (currentItem) => {
        transformed.push({
          ...item,
          ...currentItem,
          key: `${item.key}-1`,
          subRow: "Detail 1",
        });
      });
      if (item.detail?.length > 0) {
        transformed.push({
          // ...item,
          key: `${item.key}-separator`,
          isSeparator: true,
        });
      }
    });
    return transformed;
  };

  const renderMainContent = () => {
    if (viewMode === ViewMode.AUTH) {
      return renderAuth();
    }
    return renderMonitorTable();
  };

  const rowClassName = (record: any) =>
    record.isSeparator ? "table-separator" : "";

  const renderQCStationHeader = (title: any, backgroundColor: string) => {
    return (
      <div
        className="text-white px-3 py-2 d-flex justify-content-between align-items-center"
        style={{ backgroundColor }}
      >
        <span>{title}</span>
        <span>*Latest update at {TimeUtils.toDateTime(new Date())}</span>
      </div>
    );
  };

  const renderMonitorTable = () => {
    return (
      <div className="bg-white d-flex flex-column align-items-start h-100">
        {/* Pending/Failed QC Items Table */}
        <div className="w-100" style={{ height: "70%" }}>
          {renderQCStationHeader(
            <div className="d-flex align-items-center">
              <Icon
                name="arrow_back"
                size="large"
                className="cursor-pointer"
                onClick={() => setViewMode(ViewMode.AUTH)}
              />
              <div className="bg-white px-2 py-1 text-primary flex-center ml-2">
                {stationInfo?.code}
              </div>
              <div className="ml-2">{stationInfo?.name}</div>
            </div>,
            "#007bff"
          )}
          <div style={{ maxWidth: "100vw" }}>
            <AwesomeTableComponent
              ref={(ref: any) => {
                tableRef.current = ref;
              }}
              columns={columns}
              dataSource={dataSource.filter(
                (item: any) =>
                  item?.qc_station_status !== QC_PROCESS_STATUS.SUCCESS
              )}
              loading={loading}
              rowClassName={rowClassName}
            />
          </div>
        </div>

        {/* Passed QC Items Table */}
        <div className="w-100" style={{ height: "30%" }}>
          {renderQCStationHeader(
            <div className="d-flex align-items-center">
              <div className="bg-white px-2 py-1 text-success flex-center ml-2">
                {stationInfo?.code}
              </div>
              <div className="ml-2">{stationInfo?.name}</div>
              <div className="bg-white px-2 py-1 text-success flex-center ml-2">
                Passed
              </div>
            </div>,
            "#20AA14"
          )}
          <div style={{ maxWidth: "100vw" }}>
            <AwesomeTableComponent
              columns={columns}
              dataSource={dataSource.filter(
                (item: any) =>
                  item?.qc_station_status === QC_PROCESS_STATUS.SUCCESS
              )}
              loading={loading}
              rowClassName={rowClassName}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderAuth = () => {
    return (
      <div className="d-flex bg-white shadow">
        <div
          className="d-flex flex-column align-items-center justify-content-center bg-white px-4 py-4 w-sm-auto position-relative"
          style={{ height: "500px", width: "350px" }}
        >
          <div className=" w-100 ">
            <form onSubmit={authForm.handleSubmit}>
              <InputText
                id="login-username"
                name="username"
                placeholder="Enter username"
                label="User name"
                onChange={authForm.handleChange}
                error={authForm.errors.username as any}
                className="mt-3"
              />
              <InputTextPassword
                id="login-password"
                type="password"
                name="password"
                placeholder="Password"
                label="Password"
                onChange={authForm.handleChange}
                error={authForm.errors.password as any}
                className="mt-3"
              />

              <Button
                type="submit"
                className="mt-5 w-100"
                // onClick={() => formik.handleSubmit()}
              >
                Login
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AppLayoutContext>
      <div
        className="d-flex justify-content-center align-items-center bg-muted "
        style={{ height: "100vh" }}
      >
        {renderMainContent()}
      </div>
    </AppLayoutContext>
  );
}

export default AppLayout;
